// src/pages/MyMuses.js
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../auth/contexts/AuthContext';
import { PACKS } from '../../../data/packs';
import { Muse, MuseTier } from '../../../types';
import { StarService } from '../../stars/services/StarService';
import RarityGem from '../../../shared/ui/RarityGem';
import MuseUpgradeModal from './MuseUpgradeModal';
import '../styles/Inventory.css';
import '../styles/MuseTiers.css';
import { playSound } from '../../../utils/audio';

interface MyMusesProps {
  onMuseSelect?: (muse: Muse) => void;
}

interface RarityCounts {
  Common: number;
  Uncommon: number;
  Rare: number;
  Epic: number;
  Legendary: number;
}

const MyMuses: React.FC<MyMusesProps> = ({ onMuseSelect }) => {
  const { userData, currentUser, updateUserData } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [showPacks, setShowPacks] = useState<boolean>(true);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState<boolean>(false);
  const [selectedUpgradeMuse, setSelectedUpgradeMuse] = useState<Muse | null>(null);
  const [highlightedMuse, setHighlightedMuse] = useState<string | null>(null);
  const [now, setNow] = useState<Date>(new Date());

  // Derive state directly from props/context
  const muses = userData?.muses || [];
  const selectedMuse = userData?.selectedMuse || null;
  const userStars = userData?.stars || 0;

  useEffect(() => {
    if (userData || currentUser === null) {
      setLoading(false);
    }
  }, [userData, currentUser]);

  // Tick for countdowns
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const handleSelectMuse = async (muse: Muse): Promise<void> => {
    if (!userData || !currentUser) return;
    
    try {
      // Optimistically update UI, though AuthContext will handle the source of truth
      if (onMuseSelect) {
        onMuseSelect(muse);
      }
      
      // Update selected muse using Firestore directly
      // This will trigger the onSnapshot listener in AuthContext and update userData
      const { doc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('../../../shared/storage/firebase');
      
      const userRef = doc(db, 'users', userData.id);
      await updateDoc(userRef, { selectedMuse: muse });
      
      // Update local state immediately for instant UI refresh
      updateUserData({ selectedMuse: muse });

      // Reward-only SFX: confirming a new active muse
      try {
        playSound('/audio/jingle-select.mp3', 0.5);
      } catch (e) {
        console.warn('Failed to play muse select sound', e);
      }
      
    } catch (error) {
      console.error('Error selecting muse:', error);
      void window.appAlert('Failed to select muse.');
    }
  };

  // Build a map of unlocked muses by name for quick lookup
  const unlockedMap: Record<string, Muse> = {};
  muses.forEach(m => {
    if (m.name) {
      unlockedMap[m.name] = m;
    }
  });

  // Build counts per muse name (duplicates supported)
  const countsByName: Record<string, number> = muses.reduce((acc: Record<string, number>, m) => {
    if (!m.name) return acc;
    acc[m.name] = (acc[m.name] || 0) + 1;
    return acc;
  }, {});

  // Rarity order for sorting
  const rarityOrder: Record<string, number> = { 
    Legendary: 0, 
    Epic: 1, 
    Rare: 2, 
    Uncommon: 3, 
    Common: 4 
  };

  // Muse Score calculation
  const rarityCounts: RarityCounts = { 
    Common: 0, 
    Uncommon: 0, 
    Rare: 0, 
    Epic: 0, 
    Legendary: 0 
  };
  
  muses.forEach(m => {
    if (m.rarity && rarityCounts[m.rarity as keyof RarityCounts] !== undefined) {
      rarityCounts[m.rarity as keyof RarityCounts]++;
    }
  });
  
  const total = Object.values(rarityCounts).reduce((a: number, b: number) => a + b, 0);

  // For Show All: flatten all packs into a single array and sort by rarity
  const allMuses = Object.entries(PACKS).flatMap(([packId, pack]) =>
    pack.muses.map(m => ({ ...m, pack: packId }))
  ).sort((a, b) => rarityOrder[a.rarity] - rarityOrder[b.rarity]);

  // Compute right padding for sticky banner to avoid overlapping the Muse Score panel on wide screens
  const stickyRight = (typeof window !== 'undefined' && window.innerWidth > 1100) ? 300 : 16;

  const handleKeyDown = (e: React.KeyboardEvent, muse: Muse): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleSelectMuse(muse);
    }
  };

  const handleMuseClick = (muse: Muse): void => {
    // Always select the muse first
    handleSelectMuse(muse);
  };

  const handleUpgradeClick = (muse: Muse, event: React.MouseEvent): void => {
    event.stopPropagation(); // Prevent triggering the muse selection


    // Check if this muse can be upgraded
    const canUpgrade = StarService.canUpgradeMuse(muse.tier);

    if (canUpgrade && (muse.tier || 1) < 3) {
      // Show upgrade highlight and open modal
      setHighlightedMuse(muse.name);
      setSelectedUpgradeMuse(muse);
      setUpgradeModalOpen(true);

    }
  };

  const handleUpgradeComplete = (upgradedMuse: Muse, starsCost: number): void => {
    // Update the muse in userData
    if (userData && userData.muses) {
      const updatedMuses = userData.muses.map(m =>
        m.name === upgradedMuse.name ? upgradedMuse : m
      );
      updateUserData({
        muses: updatedMuses,
        stars: userStars - starsCost
      });
    }

    // Clear highlight and close modal
    setHighlightedMuse(null);
    setSelectedUpgradeMuse(null);
    setUpgradeModalOpen(false);
  };

  const handleUpgradeModalClose = (): void => {
    setHighlightedMuse(null);
    setSelectedUpgradeMuse(null);
    setUpgradeModalOpen(false);
  };


  if (loading) {
    return (
      <div className="inventory-loading">
        <div className="inventory-loading-spinner">🔄</div>
        <p>Loading your collection...</p>
      </div>
    );
  }

  return (
    <div className="inventory-container" style={{ position: 'relative' }}>
      {/* Title and toggle row */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ fontFamily: '"Fredoka One", "Arial Rounded MT Bold", Arial, sans-serif', fontWeight: 900, fontSize: '2.5rem', color: '#2196f3', marginRight: '2.5rem', marginBottom: 0 }}>My Muses</h1>
        <div className="toggle-box" style={{ background: '#e3f4fd', borderRadius: '1.5rem', padding: '0.3rem', display: 'inline-flex', boxShadow: '0 2px 8px rgba(79,195,247,0.10)' }}>
          <button
            className={showPacks ? 'toggle-btn active' : 'toggle-btn'}
            style={{ borderRadius: '1.2rem 0 0 1.2rem' }}
            onClick={() => setShowPacks(true)}
            aria-pressed={showPacks}
          >
            Show Packs
          </button>
          <button
            className={!showPacks ? 'toggle-btn active' : 'toggle-btn'}
            style={{ borderRadius: '0 1.2rem 1.2rem 0' }}
            onClick={() => setShowPacks(false)}
            aria-pressed={!showPacks}
          >
            Show All
          </button>
        </div>
      </div>
      
      {/* Muse Score panel on the right, fixed */}
      <div 
        style={{
          position: 'fixed',
          top: '50%',
          right: 40,
          transform: 'translateY(-50%)',
          zIndex: 1200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          minWidth: 240,
        }}
        className="muse-score-panel"
      >
        <div style={{
          background: 'linear-gradient(135deg, #e3f4fd 0%, #ede7f6 100%)',
          borderRadius: '1.5rem',
          boxShadow: '0 2px 16px rgba(79,195,247,0.10)',
          padding: '1.5rem 2.5rem 1.2rem 2.5rem',
          minWidth: 220,
          maxWidth: 320,
          textAlign: 'left',
          fontFamily: 'Fredoka One, Arial Rounded MT Bold, Arial, sans-serif',
          fontWeight: 700,
        }}>
          <div className="score-title" style={{ fontWeight: 700, color: '#8e24aa', fontSize: '1.1rem', marginBottom: 8 }}>Muse Score</div>
          <div className="score-total" style={{ fontWeight: 900, fontSize: '1.5rem', marginBottom: 8 }}>{total} Total</div>
          <div className="score-breakdown">
            {Object.entries(rarityCounts).map(([rarity, count]) => (
              <div key={rarity} className="score-item" style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                <RarityGem 
                  rarity={rarity} 
                  style={{ marginRight: 8 }}
                />
                <span style={{ fontWeight: 700, color: '#333', minWidth: 80 }}>{rarity}</span>
                <span style={{ fontWeight: 900, color: '#8e24aa', marginLeft: 'auto' }}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Packs or All muses grid, inside a colored rectangle */}
      <div style={{
        background: 'linear-gradient(135deg, #e3f4fd 0%, #ede7f6 100%)',
        borderRadius: '2rem',
        boxShadow: '0 4px 24px rgba(79,195,247,0.10)',
        padding: '2.5rem 2rem',
        margin: '0 auto',
        maxWidth: 1200,
        minWidth: 320,
        fontFamily: 'Fredoka One, Arial Rounded MT Bold, Arial, sans-serif',
        fontWeight: 700,
      }}>
        {showPacks ? (
          Object.entries(PACKS).map(([packId, pack]) => (
            <div key={packId} className="inventory-section" style={{ marginBottom: '2.5rem' }}>
              <h2 className="inventory-title" style={{ color: '#2196f3', fontWeight: 900, fontSize: '2rem', marginBottom: '1rem', textAlign: 'center', fontFamily: 'Fredoka One, Arial Rounded MT Bold, Arial, sans-serif' }}>{pack.name}</h2>
              <div className="muses-row center-muses" role="list" aria-label={`${pack.name} muses`}>
                {pack.muses.map((m, i) => {
                  const unlocked = unlockedMap[m.name];
                  const isSelected = selectedMuse && selectedMuse.name === m.name;
                  const isHighlighted = highlightedMuse === m.name;
                  const canUpgrade = unlocked && StarService.canUpgradeMuse(unlocked.tier);
                  const count = countsByName[m.name] || 0;
                  const tierClass = unlocked ? `muse-tier-${unlocked.tier || 1}` : '';

                  return (
                    <div
                      key={i}
                      className={`muse-circle-card${unlocked ? ' unlocked' : ' locked'}${isSelected ? ' selected' : ''}${isHighlighted ? ' muse-upgrade-highlight' : ''} ${tierClass}`}
                      onClick={() => unlocked && handleMuseClick(unlocked)}
                      tabIndex={unlocked ? 0 : -1}
                      aria-label={unlocked ? `${m.name} ${m.rarity}${canUpgrade ? ' - Can upgrade' : ''}` : `Locked ${m.rarity} muse`}
                      role="listitem"
                      onKeyDown={(e) => unlocked && handleKeyDown(e, unlocked)}
                      style={{ fontFamily: 'Fredoka One, Arial Rounded MT Bold, Arial, sans-serif', fontWeight: 700 }}
                    >
                      <RarityGem 
                        rarity={m.rarity} 
                        className="muse-rarity-gem" 
                        style={{ position: 'absolute', top: 8, left: 8, zIndex: 2, pointerEvents: 'none' }}
                      />
                      {/* Count badge (top-right) for duplicates */}
                      {unlocked && count > 1 && (
                        <div
                          aria-label={`You own ${count} of ${m.name}`}
                          title={`You own ${count}`}
                          style={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            zIndex: 2,
                            background: '#ff7a00',
                            color: '#fff',
                            border: '2px solid rgba(255,255,255,0.85)',
                            borderRadius: 999,
                            padding: '0.05rem 0.4rem',
                            fontSize: 12,
                            fontWeight: 900,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                          }}
                        >
                          ×{count}
                        </div>
                      )}
                      <div className="muse-emoji-main-circle">
                        {unlocked ? (
                          <img
                            src={m.img}
                            alt={m.name}
                            style={{
                              width: 72,
                              height: 72,
                              filter: 'drop-shadow(0 6px 12px rgba(0, 0, 0, 0.3)) drop-shadow(0 3px 6px rgba(0, 0, 0, 0.2)) drop-shadow(0 1px 3px rgba(0, 0, 0, 0.1))',
                              borderRadius: '6px'
                            }}
                          />
                        ) : (
                          <span role="img" aria-label="locked">🔒</span>
                        )}
                      </div>
                      {unlocked && canUpgrade && (
                        <div
                          className="muse-upgrade-overlay"
                          onClick={(e) => handleUpgradeClick(unlocked, e)}
                        >
                          ⭐ Upgrade?
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div className="muses-all-grid center-muses" role="list" aria-label="All muses">
            {allMuses.map((m, i) => {
              const unlocked = unlockedMap[m.name];
              const isSelected = selectedMuse && selectedMuse.name === m.name;
              const isHighlighted = highlightedMuse === m.name;
              const canUpgrade = unlocked && StarService.canUpgradeMuse(unlocked.tier);
              const count = countsByName[m.name] || 0;
              const tierClass = unlocked ? `muse-tier-${unlocked.tier || 1}` : '';

              return (
                <div
                  key={i}
                  className={`muse-circle-card${unlocked ? ' unlocked' : ' locked'}${isSelected ? ' selected' : ''}${isHighlighted ? ' muse-upgrade-highlight' : ''} ${tierClass}`}
                  onClick={() => unlocked && handleMuseClick(unlocked)}
                  tabIndex={unlocked ? 0 : -1}
                  aria-label={unlocked ? `${m.name} ${m.rarity}${canUpgrade ? ' - Can upgrade' : ''}` : `Locked ${m.rarity} muse`}
                  role="listitem"
                  onKeyDown={(e) => unlocked && handleKeyDown(e, unlocked)}
                  style={{ fontFamily: 'Fredoka One, Arial Rounded MT Bold, Arial, sans-serif', fontWeight: 700 }}
                >
                  <RarityGem 
                    rarity={m.rarity} 
                    className="muse-rarity-gem" 
                    style={{ position: 'absolute', top: 8, left: 8, zIndex: 2, pointerEvents: 'none' }}
                  />
                  {/* Count badge (top-right) for duplicates */}
                  {unlocked && count > 1 && (
                    <div
                      aria-label={`You own ${count} of ${m.name}`}
                      title={`You own ${count}`}
                      style={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        zIndex: 2,
                        background: '#ff7a00',
                        color: '#fff',
                        border: '2px solid rgba(255,255,255,0.85)',
                        borderRadius: 999,
                        padding: '0.05rem 0.4rem',
                        fontSize: 12,
                        fontWeight: 900,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                      }}
                    >
                      ×{count}
                    </div>
                  )}
                  <div className="muse-emoji-main-circle">
                    {unlocked ? (
                      <img src={m.img} alt={m.name} style={{
                        width: 72,
                        height: 72,
                        filter: 'drop-shadow(0 6px 12px rgba(0, 0, 0, 0.3)) drop-shadow(0 3px 6px rgba(0, 0, 0, 0.2)) drop-shadow(0 1px 3px rgba(0, 0, 0, 0.1))',
                        borderRadius: '6px'
                      }} />
                    ) : (
                      <span role="img" aria-label="locked">🔒</span>
                    )}
                  </div>
                  {unlocked && canUpgrade && (
                    <div
                      className="muse-upgrade-overlay"
                      onClick={(e) => handleUpgradeClick(unlocked, e)}
                    >
                      ⭐ Upgrade?
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      {upgradeModalOpen && selectedUpgradeMuse && (
        <MuseUpgradeModal
          isOpen={upgradeModalOpen}
          onClose={handleUpgradeModalClose}
          muse={selectedUpgradeMuse}
          userStars={userStars}
          onUpgradeComplete={handleUpgradeComplete}
          userId={userData?.id}
        />
      )}
    </div>
  );
};

export default MyMuses;
